import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconReceipt } from '@tabler/icons-react';
import type {
  LineItemSyncResponse,
  LineItemsSyncedPayload,
  ResolvedPricingProfile,
} from '@techgitter/contracts';
import { resolvePricingProfile } from '@techgitter/contracts';
import type { WorkItem } from '../workitems/workItemTypes';
import { LineItemCard } from '../tickets/LineItemCard';
import { TicketInvoicePrompt } from '../invoices/TicketInvoicePrompt';
import { useWorkItemLineItems } from '../../hooks/useWorkItemLineItems';
import { useSignalREvent } from '../../hooks/useSignalR';
import { useProductCatalog } from '../../hooks/useProductCatalog';
import { usePricingProfilesConfig } from '../../hooks/usePricingProfilesConfig';
import { usePublicHolidays } from '../../hooks/usePublicHolidays';
import { useAfterHoursSuggestion } from '../../hooks/useAfterHoursSuggestion';
import { getCustomerIndexCached } from '../../services/customerIndexCacheService';
import './WorkItemPanel.css';

interface LineItemsSectionProps {
  workItem: WorkItem;
  onWorkItemUpdated: (id: string, updates: Partial<WorkItem>) => void;
  /** Increment to trigger "add line item" from the panel's + button. */
  addSignal?: number;
  /** Increment to trigger a RepairShopr sync from the panel's Sync icon. */
  syncSignal?: number;
  /** Reports the current line-item count + total up to the panel header summary. */
  onSummaryChange?: (summary: { count: number; total: number }) => void;
  /** RepairShopr customer id (resolved by the container) — required by the
   *  Create Invoice prompt. */
  rsCustomerId?: number;
}

/**
 * Full line-items editor for the V2 work-item panel. Reuses the same
 * useWorkItemLineItems hook + LineItemCard + product/pricing/after-hours setup
 * as the legacy expansion panel, scoped to add/edit/delete/sync/save/totals.
 * The surrounding chrome is styled to the V2 panel aesthetic; the per-row
 * LineItemCard is the shared legacy component.
 */
export const LineItemsSection: React.FC<LineItemsSectionProps> = ({
  workItem,
  onWorkItemUpdated,
  addSignal,
  syncSignal,
  onSummaryChange,
  rsCustomerId,
}) => {
  const jobGuid = workItem.jobGUID ?? workItem.guid;
  const dataverseCustomerId = workItem.customerId || null;
  const hasLinkedTicket = !!workItem.linkedTicketId;

  const handleLineItemsSynced = useCallback(
    (response: LineItemSyncResponse) => {
      onWorkItemUpdated(workItem.id, {
        rsLineItems: response.lineItems,
        lineItemOverrides: response.overrides,
        lineItemsSyncedAt: response.syncedAt,
        lineItems: undefined,
      });
    },
    [onWorkItemUpdated, workItem.id],
  );

  const selfSyncRef = useRef(false);
  const syncRequestedRef = useRef<() => void>(() => {});

  const {
    lineItems,
    expandedItems: lineItemExpanded,
    total: lineItemsTotal,
    isDirty: lineItemsDirty,
    isSaving: lineItemsSaving,
    isSyncing,
    saveError: lineItemsSaveError,
    onAddLineItem,
    onUpdateLineItem,
    onDeleteLineItem,
    onExpandChange: onLineItemExpandChange,
    onSave: onSaveLineItems,
    provisionLicense,
    syncError,
    lastSyncedAt,
    onSync,
    hasRsData,
    validationErrorItemId,
  } = useWorkItemLineItems({
    jobGuid,
    onSyncRequested: useCallback(() => { syncRequestedRef.current(); }, []),
    rsLineItems: workItem.rsLineItems,
    lineItemOverrides: workItem.lineItemOverrides,
    lineItemsSyncedAt: workItem.lineItemsSyncedAt,
    invoices: workItem.invoices,
    onSynced: handleLineItemsSynced,
  });

  const wrappedOnSync = useCallback(async () => {
    selfSyncRef.current = true;
    await onSync();
  }, [onSync]);
  syncRequestedRef.current = wrappedOnSync;

  // Echo-suppressed sync broadcasts (other users / orchestrator cron).
  useSignalREvent(
    'jobs:line-items-synced',
    useCallback(
      (data: unknown) => {
        const payload = data as LineItemsSyncedPayload;
        if (payload.jobGuid === jobGuid) {
          if (selfSyncRef.current) {
            selfSyncRef.current = false;
            return;
          }
          onSync();
        }
      },
      [jobGuid, onSync],
    ),
  );

  // Product catalog + pricing (needed by LineItemCard).
  const { products } = useProductCatalog();
  const { config: pricingProfilesConfig } = usePricingProfilesConfig();
  const [resolvedPricing, setResolvedPricing] = useState<ResolvedPricingProfile | undefined>();
  useEffect(() => {
    if (!dataverseCustomerId) return;
    let cancelled = false;
    getCustomerIndexCached()
      .then((result) => {
        if (cancelled) return;
        const match = result.items.find((c) => c.dataverseCustomerId === dataverseCustomerId);
        if (match && pricingProfilesConfig && products.length > 0) {
          setResolvedPricing(
            resolvePricingProfile(
              pricingProfilesConfig,
              match.customerType ?? 'Business',
              match.designation ?? undefined,
              products,
            ),
          );
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [dataverseCustomerId, pricingProfilesConfig, products]);

  // After-hours pricing context.
  const { holidays } = usePublicHolidays();
  const afterHoursSuggestion = useAfterHoursSuggestion(holidays);
  const afterHoursContext = useMemo(
    () =>
      afterHoursSuggestion.isAfterHours
        ? {
            isAfterHours: true as const,
            window: afterHoursSuggestion.window,
            reason: afterHoursSuggestion.reason,
          }
        : undefined,
    [afterHoursSuggestion],
  );

  const invoicePaid = useMemo(
    () => workItem.invoices?.some((inv) => inv.is_paid) ?? false,
    [workItem.invoices],
  );
  const invoiceId = useMemo(() => {
    const invs = workItem.invoices;
    if (!invs || invs.length === 0) return undefined;
    return (invs.find((inv) => inv.is_paid) ?? invs[0]).id;
  }, [workItem.invoices]);

  // Create Invoice gating — mirrors the legacy WorkItemExpansionPanel: the
  // green Create Invoice button shows when the work item has a linked
  // RepairShopr ticket and at least one chargeable, not-yet-invoiced line item.
  const [invoicePromptOpen, setInvoicePromptOpen] = useState(false);
  const hasUnpaidItems = lineItems.some((li) => !li.converted && !li.nonChargeable);
  const hasPendingLineItems = lineItems.some((li) => li.pending);
  const createInvoiceDisabledReason = isSyncing
    ? 'Syncing line items…'
    : lineItemsSaving
      ? 'Saving line items…'
      : lineItemsDirty
        ? 'Save line items before creating an invoice'
        : hasPendingLineItems
          ? 'Waiting for RepairShopr to confirm the new items — try again in a few seconds'
          : null;

  // External "+" trigger from the panel header.
  const lastAddSignal = useRef(addSignal);
  useEffect(() => {
    if (addSignal === undefined || addSignal === lastAddSignal.current) return;
    lastAddSignal.current = addSignal;
    onAddLineItem();
  }, [addSignal, onAddLineItem]);

  // External Sync trigger from the panel header.
  const lastSyncSignal = useRef(syncSignal);
  useEffect(() => {
    if (syncSignal === undefined || syncSignal === lastSyncSignal.current) return;
    lastSyncSignal.current = syncSignal;
    void wrappedOnSync();
  }, [syncSignal, wrappedOnSync]);

  // Report count + total up to the panel header summary.
  useEffect(() => {
    onSummaryChange?.({ count: lineItems.length, total: lineItemsTotal });
  }, [lineItems.length, lineItemsTotal, onSummaryChange]);

  return (
    <>
    <div className="line-items">
      {(lineItemsSaveError || syncError) && (
        <div className="line-items__error">{lineItemsSaveError || syncError}</div>
      )}

      {!hasRsData && hasLinkedTicket && lineItems.length === 0 && !lastSyncedAt && (
        <div className="line-items__hint">
          Line items haven&apos;t been synced from RepairShopr yet.
        </div>
      )}

      {lineItems.length === 0 && !hasLinkedTicket && (
        <div className="line-items__hint">No line items</div>
      )}

      <div className="line-items__cards">
        {lineItems.map((item, index) => (
          <LineItemCard
            key={item.id}
            index={index}
            lineItem={item}
            expanded={!!lineItemExpanded[item.id]}
            canDelete={!item.converted || !invoicePaid}
            invoicePaid={invoicePaid}
            invoiceId={invoiceId}
            autoFocus={item.source === 'manual' && !item.lineItemId && !!lineItemExpanded[item.id]}
            quantityError={validationErrorItemId === item.id}
            onExpandChange={onLineItemExpandChange}
            onUpdate={onUpdateLineItem}
            onDelete={onDeleteLineItem}
            onProvisionLicense={
              dataverseCustomerId && jobGuid
                ? (id) => provisionLicense(id, dataverseCustomerId)
                : undefined
            }
            products={products}
            resolvedPricingProfile={resolvedPricing}
            afterHoursContext={afterHoursContext}
            variant="v2"
          />
        ))}
      </div>

      {lineItems.length > 0 && (
        <div className="line-items__save">
          {hasLinkedTicket && hasUnpaidItems && (
            <button
              type="button"
              className="line-items__invoice-btn"
              onClick={() => setInvoicePromptOpen(true)}
              disabled={createInvoiceDisabledReason !== null}
              title={createInvoiceDisabledReason ?? 'Create invoice'}
            >
              <IconReceipt size={16} stroke={1.5} />
              Create Invoice
            </button>
          )}
          <button
            type="button"
            className="save-btn"
            onClick={onSaveLineItems}
            disabled={!lineItemsDirty || lineItemsSaving}
          >
            {lineItemsSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
    </div>
    {invoicePromptOpen && (
      <TicketInvoicePrompt
        open={invoicePromptOpen}
        onClose={() => setInvoicePromptOpen(false)}
        lineItems={workItem.rsLineItems ?? []}
        rsCustomerId={rsCustomerId ?? 0}
        rsTicketId={Number(workItem.id) || 0}
        dvJobGuid={jobGuid}
      />
    )}
    </>
  );
};
